import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../services/api";
import { Card, CardContent } from "../components/ui/Card";
import { Ticket, CheckCircle, Clock, XCircle, Gauge, Smile, UserCheck } from "lucide-react";
import Layout from "../components/Layout";
import { usePermissions } from "../context/PermissionsContext";
import { parseApiDateTime } from "../utils/datetime";

// Estrelas 0..5 (arredonda o valor para preencher).
const Stars = ({ value = 0 }) => {
  const v = Math.round(Number(value) || 0);
  return (
    <span className="text-lg tracking-tight" aria-label={`${value} de 5`}>
      {[1, 2, 3, 4, 5].map((n) => (
        <span key={n} className={n <= v ? "text-amber-500" : "text-gray-300"}>★</span>
      ))}
    </span>
  );
};

const statusBadge = {
  Aberto: "bg-blue-100 text-blue-700",
  "Em Andamento": "bg-yellow-100 text-yellow-700",
  "Aguardando aprovação": "bg-cyan-100 text-cyan-700",
  Encerrado: "bg-green-100 text-green-700",
  Cancelado: "bg-gray-100 text-gray-600",
};

const priorityBadge = {
  Baixa: "bg-gray-100 text-gray-600",
  Media: "bg-blue-100 text-blue-700",
  "Média": "bg-blue-100 text-blue-700",
  Alta: "bg-orange-100 text-orange-700",
  Urgente: "bg-red-100 text-red-700",
};

const fmtDate = (v) => {
  const d = parseApiDateTime(v);
  if (!d) return "";
  return d.toLocaleString("pt-BR", {
    timeZone: "America/Sao_Paulo",
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const Dashboard = () => {
  const [stats, setStats] = useState({ open: 0, in_progress: 0, closed: 0, cancelled: 0, sla: {}, csat: {} });
  const [recentTickets, setRecentTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { can } = usePermissions();
  // Só mostra a lista de chamados recentes (títulos + solicitantes) a quem pode
  // ver Chamados — evita vazar conteúdo de chamado pra perfil sem essa permissão.
  const canViewTickets = can("view", "/tickets");

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        setLoading(true);
        const [statsRes, recentRes] = await Promise.all([
          api.get("/tickets/stats/dashboard"),
          canViewTickets
            ? api.get("/tickets/", { params: { skip: 0, limit: 5 } })
            : Promise.resolve({ data: [] }),
        ]);
        if (!alive) return;
        const s = statsRes.data || {};
        setStats({
          open: s.open || 0,
          in_progress: s.in_progress || 0,
          closed: s.closed || 0,
          cancelled: s.cancelled || 0,
          sla: s.sla || {},
          csat: s.csat || {},
        });
        setRecentTickets(Array.isArray(recentRes.data) ? recentRes.data : []);
      } catch (error) {
        console.error("Erro ao carregar dashboard:", error);
        if (alive) {
          setStats({ open: 0, in_progress: 0, closed: 0, cancelled: 0, sla: {}, csat: {} });
          setRecentTickets([]);
        }
      } finally {
        if (alive) setLoading(false);
      }
    };
    load();
    return () => {
      alive = false;
    };
  }, [canViewTickets]);

  const statCards = [
    { title: "Chamados Abertos", value: stats.open || 0, icon: Ticket, color: "text-blue-600", bgColor: "bg-blue-100", status: "Aberto" },
    { title: "Em Andamento", value: stats.in_progress || 0, icon: Clock, color: "text-yellow-600", bgColor: "bg-yellow-100", status: "Em Andamento" },
    { title: "Encerrados", value: stats.closed || 0, icon: CheckCircle, color: "text-green-600", bgColor: "bg-green-100", status: "Encerrado" },
    { title: "Cancelados", value: stats.cancelled || 0, icon: XCircle, color: "text-red-600", bgColor: "bg-red-100", status: "Cancelado" },
  ];

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-500">Carregando...</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
          <p className="text-gray-500">Visão geral do sistema</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {statCards.map((stat, index) => {
            const Icon = stat.icon;
            return (
              <Card
                key={index}
                role="button"
                tabIndex={0}
                onClick={() => navigate(`/tickets?status=${encodeURIComponent(stat.status)}`)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    navigate(`/tickets?status=${encodeURIComponent(stat.status)}`);
                  }
                }}
                className="cursor-pointer transition-shadow hover:shadow-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                title={`Ver chamados: ${stat.title}`}
              >
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-600">{stat.title}</p>
                      <p className="text-3xl font-bold text-gray-900 mt-2">{stat.value}</p>
                    </div>
                    <div className={`p-3 rounded-full ${stat.bgColor}`}>
                      <Icon className={`w-6 h-6 ${stat.color}`} />
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        <div>
          <h2 className="text-lg font-semibold text-gray-900 mb-1">Qualidade do atendimento (últimos 30 dias)</h2>
          <p className="text-sm text-gray-500 mb-4">Indicadores de SLA e satisfação — visíveis para toda a equipe.</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-sm font-semibold text-gray-700">SLA cumprido (resolução)</p>
                    <p className="text-xs text-gray-500 mt-1 leading-relaxed">% de chamados resolvidos dentro do prazo do SLA, contado em horário comercial.</p>
                  </div>
                  <div className="p-2.5 rounded-full bg-sky-100 shrink-0"><Gauge className="w-5 h-5 text-sky-600" /></div>
                </div>
                <p className="text-3xl font-bold text-gray-900 mt-3">
                  {stats.sla?.compliance_pct == null ? "—" : `${stats.sla.compliance_pct}%`}
                </p>
                <p className="text-xs text-gray-400 mt-1">
                  {stats.sla?.met || 0} no prazo · {stats.sla?.breached || 0} violados
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-sm font-semibold text-gray-700">Satisfação geral (CSAT)</p>
                    <p className="text-xs text-gray-500 mt-1 leading-relaxed">Nota média (1–5) que os solicitantes deram ao atendimento na pesquisa do e-mail de encerramento.</p>
                  </div>
                  <div className="p-2.5 rounded-full bg-amber-100 shrink-0"><Smile className="w-5 h-5 text-amber-600" /></div>
                </div>
                <p className="text-3xl font-bold text-gray-900 mt-3">
                  {stats.csat?.general_avg == null ? "—" : stats.csat.general_avg}
                </p>
                <div className="mt-1 flex items-center gap-2">
                  <Stars value={stats.csat?.general_avg || 0} />
                  <span className="text-xs text-gray-400">{stats.csat?.count_general || 0} avaliações</span>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-sm font-semibold text-gray-700">Satisfação com o analista</p>
                    <p className="text-xs text-gray-500 mt-1 leading-relaxed">Nota média (1–5) específica para o atendimento do analista responsável pelo chamado.</p>
                  </div>
                  <div className="p-2.5 rounded-full bg-violet-100 shrink-0"><UserCheck className="w-5 h-5 text-violet-600" /></div>
                </div>
                <p className="text-3xl font-bold text-gray-900 mt-3">
                  {stats.csat?.analyst_avg == null ? "—" : stats.csat.analyst_avg}
                </p>
                <div className="mt-1 flex items-center gap-2">
                  <Stars value={stats.csat?.analyst_avg || 0} />
                  <span className="text-xs text-gray-400">{stats.csat?.count_analyst || 0} avaliações</span>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {canViewTickets && (
        <Card>
          <CardContent className="pt-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Chamados Recentes</h2>

            {recentTickets.length === 0 ? (
              <div className="text-center py-8 text-gray-500">Nenhum chamado encontrado</div>
            ) : (
              <div className="space-y-3">
                {recentTickets.map((ticket) => (
                  <div
                    key={ticket.id}
                    onClick={() => navigate(`/tickets/${ticket.id}`)}
                    className="flex items-center justify-between gap-3 border rounded-lg p-4 hover:bg-gray-50 cursor-pointer"
                  >
                    <div className="min-w-0">
                      <div className="font-medium text-gray-900 truncate">{ticket.title}</div>
                      <div className="text-sm text-gray-500 truncate">
                        #{ticket.id} · {ticket.requester_name || "Sem solicitante"}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {ticket.priority && (
                        <span
                          className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${
                            priorityBadge[ticket.priority] || "bg-gray-100 text-gray-600"
                          }`}
                        >
                          {ticket.priority}
                        </span>
                      )}
                      {ticket.status && (
                        <span
                          className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${
                            statusBadge[ticket.status] || "bg-gray-100 text-gray-600"
                          }`}
                        >
                          {ticket.status}
                        </span>
                      )}
                      <span className="hidden sm:block text-xs text-gray-400 whitespace-nowrap">
                        {fmtDate(ticket.created_at)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
        )}
      </div>
    </Layout>
  );
};

export default Dashboard;
